import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { authApi } from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectTo, setRedirectTo] = useState('/login');
  const [companyName, setCompanyName] = useState(null);
  const [error, setError] = useState(null);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasLetter: /[a-zA-Z]/.test(password),
    match: password === confirmPassword && password.length > 0,
  };
  
  const isPasswordValid = passwordChecks.length && passwordChecks.hasNumber && passwordChecks.hasLetter && passwordChecks.match;

  useEffect(() => {
    if (!token) {
      setError('Token de réinitialisation manquant ou invalide.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await authApi.resetPassword(token, password);
      const data = result?.data || {};
      if (data.redirect_to) setRedirectTo(data.redirect_to);
      if (data.company_name) setCompanyName(data.company_name);
      setIsSuccess(true);
      toast.success('Mot de passe modifié avec succès !');
    } catch (err) {
      const message = err.response?.data?.detail || 'Une erreur est survenue. Le lien a peut-être expiré.';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold">Lien invalide</h2>
              <p className="text-muted-foreground">
                Ce lien de réinitialisation est invalide ou a expiré.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Retour à la connexion</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold">Mot de passe modifié !</h2>
              <p className="text-muted-foreground">
                Votre mot de passe a été réinitialisé avec succès. 
                {companyName
                  ? <> Vous pouvez maintenant accéder à votre espace <strong>{companyName}</strong>.</>
                  : <> Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</>}
              </p>
              <Button asChild className="w-full" data-testid="reset-password-continue-btn">
                <Link to={redirectTo}>{companyName ? `Accéder à mon espace ${companyName}` : 'Se connecter'}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">Nouveau mot de passe</CardTitle>
          <CardDescription>
            Choisissez un nouveau mot de passe sécurisé pour votre compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="new-password-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Password Validation */}
            <div className="space-y-1.5 text-sm">
              <div className={`flex items-center gap-2 ${passwordChecks.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                {passwordChecks.length ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border" />}
                Au moins 8 caractères
              </div>
              <div className={`flex items-center gap-2 ${passwordChecks.hasLetter ? 'text-green-600' : 'text-muted-foreground'}`}>
                {passwordChecks.hasLetter ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border" />}
                Contient des lettres
              </div>
              <div className={`flex items-center gap-2 ${passwordChecks.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                {passwordChecks.hasNumber ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border" />}
                Contient des chiffres
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  data-testid="confirm-password-input"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && !passwordChecks.match && (
                <p className="text-sm text-red-500">Les mots de passe ne correspondent pas</p>
              )}
              {passwordChecks.match && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Les mots de passe correspondent
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              data-testid="reset-password-btn"
              disabled={!isPasswordValid || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Modification en cours...
                </>
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>

            {/* Back to login */}
            <Button variant="ghost" asChild className="w-full">
              <Link to="/login" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </Link>
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
